import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, useMutation, useQueryClient } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { authClient } from "@apps/dashboard/lib/auth-client";
import { afterDashboardSignOut } from "@apps/dashboard/lib/after-dashboard-sign-out";
import { ApiRequestError } from "@apps/dashboard/lib/api-fetch";
import { deleteUserAccount } from "@apps/dashboard/lib/home-expense-api";
import { dashboardPaths } from "@apps/dashboard/lib/routes";

export interface AccountDeleteConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Modal accesible para confirmar eliminación permanente de cuenta (reutilizable en /settings y datos personales).
 */
export function AccountDeleteConfirmDialog({
  open,
  onOpenChange,
}: AccountDeleteConfirmDialogProps): JSX.Element | null {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const deleteAccountMutation = useMutation({
    mutationFn: async () => deleteUserAccount(locale),
    onSuccess: async () => {
      onOpenChange(false);
      await authClient.signOut();
      afterDashboardSignOut(queryClient);
      navigate(dashboardPaths.login, { replace: true });
    },
  });

  useEffect(() => {
    if (open) {
      deleteAccountMutation.reset();
    }
    // Solo al abrir: limpiar error/estado de un intento anterior.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset al togglear `open`
  }, [open]);

  if (!open) {
    return null;
  }

  const deleteErrorMessage =
    deleteAccountMutation.error instanceof ApiRequestError
      ? deleteAccountMutation.error.message
      : t("dashboardUi.personalDataPage.deleteAccountError");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onClick={() => {
        if (deleteAccountMutation.isPending) return;
        deleteAccountMutation.reset();
        onOpenChange(false);
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-account-dialog-title"
        className="max-w-md rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-xl"
        onClick={(ev) => ev.stopPropagation()}
      >
        <h2 id="delete-account-dialog-title" className="font-headline text-lg font-bold text-error">
          {t("dashboardUi.personalDataPage.deleteAccountModalTitle")}
        </h2>
        <p className="mt-2 font-body text-body-md text-on-surface-variant">
          {t("dashboardUi.personalDataPage.deleteAccountModalBody")}
        </p>
        {deleteAccountMutation.isError ? (
          <p className="mt-3 rounded-lg bg-error/10 px-3 py-2 text-sm text-error" role="alert">
            {deleteErrorMessage}
          </p>
        ) : null}
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            disabled={deleteAccountMutation.isPending}
            onClick={() => {
              deleteAccountMutation.reset();
              onOpenChange(false);
            }}
          >
            {t("dashboardUi.personalDataPage.deleteAccountModalCancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="rounded-xl"
            disabled={deleteAccountMutation.isPending}
            onClick={() => void deleteAccountMutation.mutate()}
          >
            {deleteAccountMutation.isPending
              ? t("dashboardUi.personalDataPage.deleteAccountPending")
              : t("dashboardUi.personalDataPage.deleteAccountModalConfirm")}
          </Button>
        </div>
      </div>
    </div>
  );
}
